import { thunk } from 'easy-peasy';
import { API } from '@aws-amplify/api';
import { graphqlOperation } from '@aws-amplify/api-graphql';
import awsApiConfig from './config/awsApiConfigure';

API.configure(awsApiConfig);

const WorksiteMiddleware = {
  //✨✨==========================================✨✨
  //            GET LIST WORKSITE
  //✨✨==========================================✨✨
  getlistWorkSite: thunk(async (actions, payload, { getStoreActions }) => {
    const companyId = payload;

    // Guard
    if (!companyId) return [];

    // Query "safe" : pas de user fields (first_name, etc.) dans worksites
    // -> on récupère uniquement les IDs, puis on hydrate via getCollaborator
    const graphqlQuery = /* GraphQL */ `
      query GetCompanyWorksites($id: ID!) {
        getCompany(id: $id) {
          id
          name
          worksites {
            items {
              id
              name
              address
              date_planned
              updatedAt
              status
              audioUrl
              type {
                id
                name
              }

              users {
                items {
                  id
                  userID
                }
              }

              collaborators {
                items {
                  user {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `;

    // IMPORTANT : ici on gère le loader uniquement pour la "grosse" requête.
    // Pour l'hydratation (plein de getCollaborator), on évite de toggler
    // l'activity à chaque appel.
    getStoreActions().global.setActivity(true);

    try {
      const queryRes = await API.graphql(
        graphqlOperation(graphqlQuery, { id: companyId })
      );

      const worksites = queryRes?.data?.getCompany?.worksites?.items || [];

      // 1) collecter tous les IDs à hydrater
      const idsToHydrate = new Set();

      for (const ws of worksites) {
        const wsUsers = ws?.users?.items || [];
        for (const u of wsUsers) {
          if (u?.userID) idsToHydrate.add(u.userID);
        }

        const wsCollabs = ws?.collaborators?.items || [];
        for (const c of wsCollabs) {
          const collabId = c?.user?.id;
          if (collabId) idsToHydrate.add(collabId);
        }
      }

      // 2) cache pour ne pas appeler getCollaborator 50 fois sur le même id
      const cache = new Map(); // id -> userObject|null

      const getUserFromCacheOrFetch = async (id) => {
        if (!id) return null;
        if (cache.has(id)) return cache.get(id);

        // Appel cross-middleware via store actions
        // ⚠️ adapte le chemin si ton modèle easy-peasy nomme différemment le slice
        // Ex : getStoreActions().collaborator.getCollaborator(...)
        const user = await getStoreActions().middleware.CollaboratorMiddleware.getCollaborator(id);

        cache.set(id, user ?? null);
        return user ?? null;
      };

      // 3) hydrate tous les IDs (en parallèle)
      await Promise.all([...idsToHydrate].map(getUserFromCacheOrFetch));

      // 4) injecter user dans users/collaborators
      const enriched = worksites.map((ws) => {
        const usersItems = (ws?.users?.items || []).map((u) => ({
          ...u,
          user: u?.userID ? cache.get(u.userID) ?? null : null,
        }));

        const collabsItems = (ws?.collaborators?.items || []).map((c) => {
          const id = c?.user?.id;
          return {
            ...c,
            user: id ? cache.get(id) ?? null : null,
          };
        });

        return {
          ...ws,
          users: { ...(ws.users || {}), items: usersItems },
          collaborators: { ...(ws.collaborators || {}), items: collabsItems },
        };
      });

      return enriched;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            LIST WORKSITE
  //✨✨==========================================✨✨
  listWorkSites: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      listWorksites(limit: 500000) {
        items {
          id
          date_planned
          name
          status
          audioUrl
          
          company {
            id
            name
          }
          collaborators {
            items {
              user {
                id
                first_name
                last_name
                phone
              }
            }
          }
        }
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      console.log('DATA', query);

      const myData = query.data?.listWorksites.items;
      console.log('DATA', myData);

      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            LIST WORKSITE BY NAME
  //✨✨==========================================✨✨
  listWorksitesByName: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      listWorksites(filter: {name: {eq: "${payload}"}} limit: 500000) {
        items {
          id
        }
      }
    }`;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.listWorksites.items;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            UPDATE STATUS WORKSITE
  //✨✨==========================================✨✨
  updateStatusWorkSite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      updateWorksite(input: {id: "${payload.id}", status: "${payload.status}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.updateWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            UPDATE DATE WORKSITE
  //✨✨==========================================✨✨
  updateDateWorkSite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      updateWorksite(input: {id: "${payload.id}", date_planned: "${payload.date}",}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.updateWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            UPDATE AUDIO WORKSITE
  //✨✨==========================================✨✨
  updateAudioWorksite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      updateWorksite(input: {id: "${payload.id}", audioUrl: "${payload.audioUrl}",}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.updateWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            CREATE WORKSITE
  //✨✨==========================================✨✨
  createWorksite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      createWorksite(input: {
        name: "${payload.name}",
        address: "${payload.address}",
        date_planned: "${payload.date}",
        typeWorksitesId: "${payload.typeClient}",
        companyWorksitesId: "${payload.id_customer}",
        status: "En cours",
      })
      {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.createWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            UPDATE WORKSITE
  //✨✨==========================================✨✨
  updateWorksite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      updateWorksite(input: {
        id: "${payload.id}", 
        name: "${payload.name}", 
        address: "${payload.address}", 
        date_planned: "${payload.date}", 
        typeWorksitesId: "${payload.typeClient}",
      })
      {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.updateWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //           CREATE WORKSITE USERS
  //✨✨==========================================✨✨
  createWorksiteUsers: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      createWorksiteUsers(input: {userID: "${payload.id_user}", worksiteID: "${payload.id_worksite}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.createWorksiteUsers;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //           CREATE WORKSITE COLLABORATOR
  //✨✨==========================================✨✨
  createWorksiteCollaborators: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      createWorksiteCollaborators(input: {userID: "${payload.id_user}", worksiteID: "${payload.id_worksite}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.createWorksiteCollaborators;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //           GET WORKSITE LIST ORDER COUPON
  //✨✨==========================================✨✨
  getWorkSiteListOrderCoupon: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      getWorksite(id: "${payload}") {
        order_coupon {
          items {
            id
            createdAt
            name
            numero
            updatedAt
            ctm
          }
        }
      }
    }`;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.getWorksite?.order_coupon?.items;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //      GET WORKSITE LIST INTERVENTION COUPON
  //✨✨==========================================✨✨
  getWorkSiteListInterventionCoupon: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      getWorksite(id: "${payload}") {
        intervention_coupon {
          items {
            createdAt
            id
            name
            numero
            ctm
            updatedAt
          }
        }
      }
    }`;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.getWorksite.intervention_coupon.items;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //    GET LIST WORKSITE USERS
  //✨✨==========================================✨✨
  getlistWorksiteUsers: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      listWorksiteUsers(filter: {worksiteID: {eq: "${payload}"}} limit: 500000) {
        items {
          id
          
        }
      }
    }`;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.listWorksiteUsers.items;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //    GET LIST WORKSITE COLLABORATORS
  //✨✨==========================================✨✨
  getlistWorksiteCollaborators: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    query MyQuery {
      listWorksiteCollaborators(filter: {worksiteID: {eq: "${payload}"}} limit: 500000) {
        items {
          id
          user {
            id
          }
        }
      }
    }`;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.listWorksiteCollaborators.items;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //    DELETE MULTIPLE WORKSITE USERS
  //✨✨==========================================✨✨
  deleteMultipleWorksiteUsers: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      ${payload.map(
      (WorksiteUser, index) => `
        task${index}: deleteWorksiteUsers(input: {id: "${WorksiteUser.id}"}) {
          id
        }
      `
    )}
    } `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.deleteWorksiteUsers;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //    DELETE MULTIPLE WORKSITE COLLABORATORS
  //✨✨==========================================✨✨
  deleteMultipleWorksiteCollaborators: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      ${payload.map(
      (WorksiteCollaborator, index) => `
        task${index}: deleteWorksiteCollaborators(input: {id: "${WorksiteCollaborator}"}) {
          id
        }
      `
    )}
    } `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.deleteWorksiteCollaborators;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //           DELETE WORKSITE USERS
  //✨✨==========================================✨✨
  deleteWorksiteUsers: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      deleteWorksiteUsers(input: {id: "${payload}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.deleteWorksiteUsers;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //           DELETE WORKSITE COLLABORATOR
  //✨✨==========================================✨✨
  deleteWorksiteCollaborators: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      deleteWorksiteCollaborators(input: {id: "${payload}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.deleteWorksiteCollaborators;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),

  //✨✨==========================================✨✨
  //            DELETE WORKSITE
  //✨✨==========================================✨✨
  deleteWorksite: thunk(async (actions, payload, { getStoreActions, getStoreState }) => {
    // body request
    const graphqlQuery = `
    mutation MyMutation {
      deleteWorksite(input: {id: "${payload}"}) {
        id
      }
    }
    `;
    getStoreActions().global.setActivity(true);
    try {
      const query = await API.graphql({
        query: graphqlQuery,
      });
      const myData = query.data?.deleteWorksite;
      return myData;
    } finally {
      getStoreActions().global.setActivity(false);
    }
  }),
};

export default WorksiteMiddleware;

